import {
  Box,
  Button,
  Dialog,
  InputLabel
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import MaterialReactTable from "material-react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { AdaptedCollectorsMissingDocuments, postMissingDocuments } from "api";

import {
  COLORS,
  H6,
  Loading,
  MultiSelectControlledOption,
  Pagination,
  ROWS_PER_PAGE_OPTIONS,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";

import {
  useGetAllowedValues,
  useGetConfigParameter,
  useSites,
  useUtilityBillsYears,
} from "hooks";

import { AddBillModal, CollectorMonthView, DataBoxes, MonthCell, SelectField } from "./components";
import { ALL_MONTHS, DATA_BOXES, MISSING_DOCUMENTS_STATIC_COLUMNS, STATE_DROPDOWN_OPTIONS } from "./consts";
import { completeMonths, getFiscalYear } from "./helpers";

export const MissingDocumentsPage = () => {
  const { data: configParameter } = useGetConfigParameter();
  const { data: availableYears } = useUtilityBillsYears();
  const { data: allowedValues } = useGetAllowedValues();
  const { data: sitesData } = useSites();
  const dataBoxes = [...DATA_BOXES];
  const allMonths = [...ALL_MONTHS];

  const {
    mutateAsync: postMissingDocumentsRequest,
    isLoading: loadingMissingDocuments,
  } = useMutation(postMissingDocuments);

  const companyMonths = useMemo(() => configParameter
    ?.filter((param: any) => param.name.includes("company_month"))
    .map((param: any) => param.configValue) ?? ["Jan", "Dec"]
    , [configParameter]);

  const allFiscalYears: string[] = useMemo(
    () =>
      (availableYears &&
        availableYears.map((fy: number) => `${fy} Fiscal Year`)) || [
        `${dayjs().get("year")} Fiscal Year`,
      ],
    [availableYears],
  );
  const countries = useMemo(
    () =>
      allowedValues
        ? allowedValues.countries.sort().map((coun: string) => ({
          name: coun.replaceAll("_", " "),
          label: coun,
          value: coun,
        }))
        : [],
    [allowedValues],
  );
  const utilityTypes = useMemo(
    () =>
      allowedValues
        ? allowedValues.utilityTypes
          .sort((utA: any, utB: any) => (utA.name > utB.name ? 1 : -1))
          .map((ut: any) => ({ name: ut.name, label: ut.name, value: ut.id }))
        : [],
    [allowedValues],
  );
  const sites = useMemo(
    () =>
      sitesData
        ? sitesData.map((site: any) => ({
          name: site.name,
          label: site.name,
          value: site.id,
        }))
        : [],
    [sitesData],
  );
  const businessUnits = useMemo(
    () =>
      allowedValues
        ? allowedValues.businessUnits
          .sort()
          .map((bu: string) => ({ name: bu, label: bu, value: bu }))
        : [],
    [allowedValues],
  );
  const countryRegions = useMemo(
    () =>
      allowedValues
        ? allowedValues.countryRegions
          .sort()
          .map((cr: string) => ({ name: cr, label: cr, value: cr }))
        : [],
    [allowedValues],
  );

  const sitesByCountry = useMemo(
    () =>
      sitesData
        ? sitesData.reduce((acc: any, site: any) => {
          if (!acc[site.country]) {
            acc[site.country] = [];
          }
          acc[site.country].push(site.id);
          return acc;
        }, [])
        : [],
    [sitesData],
  );

  const [paginationData, setPaginationData] = useState({
    currentPage: 0,
    rowsPerPage: 50,
    totalRows: 0,
    totalPages: 0
  });
  const [currentMissingData, setCurrentMissingData] = useState<any[]>([]);
  const [currentFY, setCurrentFY] = useState<string>(
    allFiscalYears[allFiscalYears.length - 1],
  );
  const [monthHeaderFY, setMonthHeaderFY] = useState<string>(
    currentFY.substring(0, 4),
  );
  const [isOnlyMissingButtonActive, setIsOnlyMissingButtonActive] = useState<boolean>(false);
  const [currentCountries, setCurrentCountries] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentSiteIds, setCurrentSiteIds] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentUtilityTypeIds, setCurrentUtilityTypeIds] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentBusinessUnits, setCurrentBusinessUnits] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentCountryRegions, setCurrentCountryRegions] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [currentStatus, setCurrentStatus] = useState<
    MultiSelectControlledOption[]
  >([]);
  const [searchActive, setSearchActive] = useState<boolean>(false);
  const [enableSearch, setEnableSeach] = useState<boolean>(false);
  const [isSitesFiltered, setIsSitesFiltered] = useState<boolean>(false);
  const [selectedMonthCell, setSelectedMonthCell] = useState<{ month: string; year: number }>({ month: '', year: Number(currentFY.substring(0, 4)) });
  const [collectorMissingDocuments, setCollectorMissingDocuments] =
    useState<AdaptedCollectorsMissingDocuments>();
  const [openMonthView, setOpenMonthView] = useState<boolean>(false);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);
  const [selectedCollectorId, setSelectedCollectorId] = useState<number | null>(null);
  const selectedfiscalYear = getFiscalYear(companyMonths, currentFY);
  const defaultObject = useRef<any>({
    siteIds: sites.map((site: any) => site.value),
    utilityTypeIds: null,
    fromDate: selectedfiscalYear.fromDate,
    toDate: selectedfiscalYear.endDate,
    businessUnits: null,
    countryRegions: null,
    onlyMissing: false,
    page: 0,
    size: 50,
    collectorStatuses: null,
  });

  const monthHeaders = useMemo(
    () => completeMonths(companyMonths, Number(monthHeaderFY)),
    [companyMonths, monthHeaderFY]
  );

  const monthColumns = monthHeaders.map(({ header: monthValue, year: monthYear }) => ({
    id: `${monthHeaderFY}-${allMonths.indexOf(monthValue.substring(0, 3)) + 1}`,
    header: monthValue,
    accessorKey: `months[${monthValue.substring(0, 3)}]`,
    Cell: ({ cell, row }: { cell: any; row: any }) => {
      const { collector, months, unit } = row.original;
      // const unit = collector.consumptionUnit; TO DO: Commented out till EFN-2382 is unblock
      const handleMonthSelectedOpen = () => {
        setSelectedMonthCell({ month: monthValue.substring(0, 3), year: monthYear });
        setCollectorMissingDocuments({
          unit,
          id: collector.id,
          collector,
          days: allMonths.reduce((acc: any, month: string) => {
            const monthIndex = allMonths.indexOf(month) + 1;
            if (!acc[monthIndex]) {
              acc[monthIndex] = months[month]
                ? months[month].days.reduce(
                  (dayAcc: any, currentDay: any) => ({
                    ...dayAcc,
                    [currentDay.day]: currentDay.usage ?? null,
                  }),
                  {},
                )
                : null;
            }
            return acc;
          }, {}),
          months: allMonths.reduce((acc: any, month: string) => {
            if (!acc[allMonths.indexOf(month) + 1]) {
              acc[allMonths.indexOf(month) + 1] = {
                usage: months[month]?.monthUsage ?? null,
                billType: months[month]?.billType ?? null,
              };
            }
            return acc;
          }, {}),
        });
        setOpenMonthView(true);
      };

      return (
        <MonthCell
          cell={cell}
          handleBillModalOpen={handleBillModalOpen}
          handleMonthSelectedOpen={handleMonthSelectedOpen}
          isMonthModalOpen={openMonthView}
        />
      );
    },
    enableSorting: false,
    minSize: 120,
    size: 120,
  }));

  const MISSING_DOCUMENTS_COLUMNS = [...MISSING_DOCUMENTS_STATIC_COLUMNS, ...monthColumns];

  const handleUpdateMissingDocuments = async (payloadFromPagination?: any) => {
    const missingDocRequestBody = payloadFromPagination ?? { ...defaultObject.current, page: 0, size: paginationData.rowsPerPage };

    await postMissingDocumentsRequest(missingDocRequestBody, {
      onSuccess: (response: any) => {
        const { data } = response;
        const paginationDataRes = data.pageResult;
        setPaginationData({
          currentPage: paginationDataRes.page,
          rowsPerPage: paginationDataRes.size,
          totalRows: paginationDataRes.totalElements,
          totalPages: paginationDataRes.totalPages
        })
        const parsedData = data.response.reduce(
          (acc: any, currentCol: any, index: number) => {
            const { collector, unit, days } = currentCol;
            acc[index] = {
              collector,
              unit,
              months: days.reduce((monthAcc: any, date: any) => {
                const currentMonth =
                  allMonths[dayjs(date.day, "YYYY-MM-DD").month()];
                const updatedMonthAcc = { ...monthAcc };

                if (!updatedMonthAcc[currentMonth]) {
                  updatedMonthAcc[currentMonth] = {};
                }

                const currentMonthData = days.filter(
                  (day: any) =>
                    dayjs(day.day, "YYYY-MM-DD").month() ===
                    allMonths.indexOf(currentMonth),
                );

                updatedMonthAcc[currentMonth] = {
                  days: currentMonthData.map(
                    ({ day, usage }: { day: string; usage: number }) => ({
                      day: day.substring(8),
                      usage,
                    }),
                  ),
                  billType: Array.from(
                    new Set(currentMonthData.map((day: any) => day.billType)),
                  )[0],
                  monthUsage: currentMonthData.reduce(
                    (usageAcc: number, day: any) => usageAcc + day.usage,
                    0,
                  ),
                };
                return updatedMonthAcc;
              }, {}),
              status: collector?.collectorStatus ?? "",
            };
            return acc;
          },
          [],
        );
        setMonthHeaderFY(currentFY.substring(0, 4));
        setCurrentMissingData(parsedData);
      },
      onError: () => {
        // implement error handling
      },
    });
    setSearchActive(false);
  };

  const onOnlyMissingButtonClick = () => {
    setIsOnlyMissingButtonActive(true);
    defaultObject.current = {
      ...defaultObject.current,
      onlyMissing: true
    };
    handleUpdateMissingDocuments();
  };

  const onShowAllButtonClick = () => {
    setIsOnlyMissingButtonActive(false);
    defaultObject.current = {
      ...defaultObject.current,
      onlyMissing: false
    }
    handleUpdateMissingDocuments();
  };

  const handleClearFilters = () => {
    setCurrentFY(allFiscalYears[allFiscalYears.length - 1]);
    setCurrentCountries([]);
    setCurrentSiteIds([]);
    setCurrentUtilityTypeIds([]);
    setCurrentBusinessUnits([]);
    setCurrentCountryRegions([]);
    setCurrentStatus([]);
    defaultObject.current = {
      siteIds: sites.map((site: any) => site.value),
      utilityTypeIds: null,
      fromDate: getFiscalYear(
        companyMonths,
        allFiscalYears[allFiscalYears.length - 1],
      ).fromDate,
      toDate: getFiscalYear(
        companyMonths,
        allFiscalYears[allFiscalYears.length - 1],
      ).endDate,
      businessUnits: null,
      countryRegions: null,
      collectorStatuses: null,
    };
    setSearchActive(true);
  };

  const updateArr = (
    arr: any[],
    value: string | number,
    unchecked?: boolean,
    isCountrySelection: boolean = false,
  ) => {
    const currentSelection = Array.from(
      new Set([...arr.map((item: any) => item.value), value]),
    );
    if (isCountrySelection) {
      setIsSitesFiltered(true);
      if (unchecked) {
        const filteredSelection = arr
          .filter((item: any) => item.value !== value)
          .map((item: any) => item.value);
        if (!filteredSelection.length) {
          setIsSitesFiltered(false);
          return null;
        }
        return filteredSelection;
      }
      if (!(currentSelection.includes(value) && !unchecked)) {
        const countriesSelected = currentSelection.filter(
          (country: any) => country !== value,
        );
        const idsByCountriesSelected = countriesSelected
          .map((country: any) => sitesByCountry[country])
          .flat()
          .filter((site: any) => site !== undefined);
        return idsByCountriesSelected;
      }
      const idsByCountriesSelected = currentSelection
        .map((country: any) => sitesByCountry[country])
        .flat()
        .filter((site: any) => site !== undefined);
      return idsByCountriesSelected;
    }
    if (arr.find((item: any) => item.value === "ALL")) {
      if (isCountrySelection) setIsSitesFiltered(false);
      return null;
    }
    if (unchecked) {
      const filteredSelection = arr
        .filter((item: any) => item.value !== value)
        .map((item: any) => item.value);
      if (!filteredSelection.length) return null;
      return filteredSelection;
    }
    return currentSelection.includes("ALL") ? null : currentSelection;
  };

  const getSitesFiltered = () =>
    sitesData
      ?.filter((site: any) =>
        currentCountries
          .map((country: any) => country.value)
          .flat()
          .includes(site.country),
      )
      .map((site: any) => ({
        name: site.name,
        label: site.name,
        value: site.id,
      })) ?? [];

  const handleBillModalOpen = (id: number | null) => {
    setSelectedCollectorId(id);
    setIsBillModalOpen(true);
  }

  const closeBillModal = () => setIsBillModalOpen(false);

  useEffect(() => {
    defaultObject.current = {
      ...defaultObject.current,
      siteIds: sites.map((site: any) => site.value),
      utilityTypeIds: null,
      fromDate: getFiscalYear(companyMonths, currentFY).fromDate,
      toDate: getFiscalYear(companyMonths, currentFY).endDate,
      businessUnits: null,
      countryRegions: null,
    };
    setEnableSeach(true);
  }, [sites]); // eslint-disable-line

  const onPageChangeHandler = (newPage: any) => {
    const updatedObject = {
      ...defaultObject.current,
      page: newPage - 1,
      size: paginationData.rowsPerPage,
    };
    handleUpdateMissingDocuments(updatedObject);
  }

  const onPageSizeChangeHandler = (newPageSize: any) => {
    const updatedObject = {
      ...defaultObject.current,
      page: paginationData.currentPage,
      size: newPageSize
    };
    handleUpdateMissingDocuments(updatedObject);
  }

  return (
    <Box sx={{ margin: "12px 24px" }}>
      <H6>Please select your filters and then search to see data below.</H6>
      <br />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <Box
            sx={{
              width: "200px",
              "& > div:first-of-type": {
                height: "35px",
              },
            }}
          >
            <InputLabel
              sx={{
                fontSize: "12px",
                color: "black",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              Fiscal Year
            </InputLabel>
            <SelectField
              options={allFiscalYears}
              initialValue={currentFY}
              onChangeFn={(event: string) => {
                setCurrentFY(event);
                defaultObject.current = {
                  ...defaultObject.current,
                  fromDate: getFiscalYear(companyMonths, event.substring(0, 4))
                    .fromDate,
                  toDate: getFiscalYear(companyMonths, event.substring(0, 4))
                    .endDate,
                };
                setSearchActive(true);
              }}
              placeholder="Select Fiscal Year"
            />
          </Box>
          <Box sx={{ width: "200px" }}>
            <EsgMultiselectControlledField
              label="Country"
              selectedValues={currentCountries}
              setSelectedValues={setCurrentCountries}
              options={countries}
              type="checkMissing"
              isAllSelectedOnEmptySelection
              onchangeFn={(
                value: string | number,
                unchecked: boolean | undefined,
              ) => {
                defaultObject.current = {
                  ...defaultObject.current,
                  siteIds:
                    updateArr(currentCountries, value, unchecked, true) ??
                    sites.map((site: any) => site.value),
                };
                setSearchActive(true);
              }}
            />
          </Box>
          <Box sx={{ width: "200px" }}>
            <EsgMultiselectControlledField
              label="Utility Type"
              selectedValues={currentUtilityTypeIds}
              setSelectedValues={setCurrentUtilityTypeIds}
              options={utilityTypes}
              type="checkMissing"
              isAllSelectedOnEmptySelection
              onchangeFn={(
                value: string | number,
                unchecked: boolean | undefined,
              ) => {
                defaultObject.current = {
                  ...defaultObject.current,
                  utilityTypeIds: updateArr(
                    currentUtilityTypeIds,
                    value,
                    unchecked,
                  ),
                };
                setSearchActive(true);
              }}
            />
          </Box>
          <Box sx={{ width: "200px" }}>
            <EsgMultiselectControlledField
              label="Sites"
              selectedValues={currentSiteIds}
              setSelectedValues={setCurrentSiteIds}
              options={!isSitesFiltered ? sites : getSitesFiltered()}
              type="checkMissing"
              isAllSelectedOnEmptySelection
              onchangeFn={(
                value: string | number,
                unchecked: boolean | undefined,
              ) => {
                defaultObject.current = {
                  ...defaultObject.current,
                  siteIds:
                    updateArr(currentSiteIds, value, unchecked) ??
                    sites.map((site: any) => site.value),
                };
                setSearchActive(true);
              }}
            />
          </Box>
          <Box sx={{ width: "200px" }}>
            <EsgMultiselectControlledField
              label="Business Unit"
              selectedValues={currentBusinessUnits}
              setSelectedValues={setCurrentBusinessUnits}
              options={businessUnits}
              type="checkMissing"
              isAllSelectedOnEmptySelection
              onchangeFn={(
                value: string | number,
                unchecked: boolean | undefined,
              ) => {
                defaultObject.current = {
                  ...defaultObject.current,
                  businessUnits: updateArr(
                    currentBusinessUnits,
                    value,
                    unchecked,
                  ),
                };
                setSearchActive(true);
              }}
            />
          </Box>
          <Box sx={{ width: "200px" }}>
            <EsgMultiselectControlledField
              label="Region"
              selectedValues={currentCountryRegions}
              setSelectedValues={setCurrentCountryRegions}
              options={countryRegions}
              type="checkMissing"
              isAllSelectedOnEmptySelection
              onchangeFn={(
                value: string | number,
                unchecked: boolean | undefined,
              ) => {
                defaultObject.current = {
                  ...defaultObject.current,
                  countryRegions: updateArr(
                    currentCountryRegions,
                    value,
                    unchecked,
                  ),
                };
                setSearchActive(true);
              }}
            />
          </Box>
          <Box sx={{ width: "200px" }}>
            <EsgMultiselectControlledField
              label="Status"
              selectedValues={currentStatus}
              setSelectedValues={setCurrentStatus}
              options={STATE_DROPDOWN_OPTIONS}
              type="checkMissing"
              isAllSelectedOnEmptySelection
              onchangeFn={(
                value: string | number,
                unchecked: boolean | undefined,
              ) => {
                defaultObject.current = {
                  ...defaultObject.current,
                  collectorStatuses: updateArr(
                    currentStatus,
                    value === "Estimates" ? "PBE" : value,
                    unchecked,
                  ),
                };
                setSearchActive(true);
              }}
            />
          </Box>
          <Box
            sx={{
              width: "296px !important",
              display: "flex",
              gap: "16px",
              mt: "22px",
            }}
          >
            <Button
              sx={{
                width: "140px",
                height: "36px",
                borderRadius: "8px",
              }}
              disabled={!enableSearch}
              variant={searchActive ? "contained" : "outlined"}
              onClick={() => handleUpdateMissingDocuments()}
            >
              Search
            </Button>
            <Button
              sx={{
                width: "140px",
                height: "36px",
                borderRadius: "8px",
              }}
              variant="outlined"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            minWidth: "fit-content",
            height: "42px",
            padding: "3px",
            border: `1px solid ${COLORS.Mainblue}`,
            borderRadius: "30px",
            marginTop: "16px",
          }}
        >
          <Button
            variant={isOnlyMissingButtonActive ? "contained" : "text"}
            sx={{ borderRadius: "30px" }}
            onClick={onOnlyMissingButtonClick}
          >
            Only Missing
          </Button>
          <Button
            variant={!isOnlyMissingButtonActive ? "contained" : "text"}
            sx={{ borderRadius: "30px" }}
            onClick={onShowAllButtonClick}
          >
            Show all
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "32px",
          flexWrap: "wrap",
          marginTop: "48px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {dataBoxes.map(({ color, title }) => <DataBoxes color={color} title={title} />)}
      </Box>
      <Box
        sx={{
          position: "relative",
          width: "calc(100vw - 151px)",
          marginTop: "32px",
          border: "1px solid #C0D0D4",
          borderRadius: "7px",
          "& > div": {
            margin: 0,
            "& > div": {
              height: "calc(100vh - 440px)",
              maxHeight: "calc(100vh - 440px)",
              overflowY: "auto",
            },
          },
          "& table": {
            "& > thead": {
              position: "sticky",
              top: 0,
              zIndex: 2,
              "& > tr > th:nth-of-type(5)": {
                borderRight: "1px solid #054254",
              },
              "& > tr > th:nth-of-type(7)": {
                borderRight: "1px solid #054254",
              },
            },
            "& > tbody": {
              "& > tr > td:nth-of-type(5)": {
                borderRight: "1px solid #054254",
              },
              "& > tr > td:nth-of-type(7)": {
                borderRight: "1px solid #054254",
              },
            },
          },
        }}
      >
        {loadingMissingDocuments ? (
          <Box
            sx={{
              "& > div: first-of-type": {
                marginTop: "100px",
                height: "auto",
              },
            }}
          >
            <Loading />
          </Box>
        ) : (
          <MaterialReactTable
            muiTableContainerProps={{
              sx: {
                border: "1px solid #C0D0D4",
                borderRadius: "7px",
              },
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "none",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                borderRight: "1px solid #C0D0D4",
                color: "#054254",
                "& .MuiBadge-root": {
                  display: "none",
                },
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                maxHeight: "60px",
                borderRight: "1px solid #C0D0D4",
                backgroundColor: "#fff",
                padding: 0,
                "& > div": {
                  maxHeight: "60px",
                },
              },
            }}
            key={companyMonths as any}
            columns={MISSING_DOCUMENTS_COLUMNS}
            data={currentMissingData}
            enableGlobalFilter={false}
            enablePagination
            manualPagination
            rowCount={paginationData.totalRows}
            enablePinning
            enableStickyHeader
            muiTablePaginationProps={{
              rowsPerPageOptions: [50, 100, 200],
              showFirstButton: true,
              showLastButton: true,
            }}
            initialState={{
              pagination: { pageSize: paginationData.rowsPerPage, pageIndex: paginationData.currentPage },
              columnPinning: {
                left: [
                  "collector.siteName",
                  "collector.providerName",
                  "collector.accountNumber",
                  "collector.meterNumber",
                  "status",
                ],
              },
            }}
          />
        )}
      </Box>
      {!(loadingMissingDocuments) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          <Pagination
            page={paginationData.currentPage + 1}
            rowsCount={paginationData.totalRows}
            rowsPerPage={paginationData.rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={onPageChangeHandler}
            onRowsPerPageChange={onPageSizeChangeHandler}
          />
        </Box>
      )}
      {openMonthView && (
        <div>
          <Dialog
            open
            sx={{
              "& > div > div": {
                width: "1480px",
                minWidth: "1480px",
                maxWidth: "1480px",
                height: "fit-content",
                maxHeight: "fit-content",
                marginLeft: "130px",
                borderRadius: "8px",
                "& > div": {
                  minHeight: "100%",
                  "& > div": {
                    padding: "0",
                    height: "100%",
                  },
                },
              },
            }}
          >
            <Box>
              <CollectorMonthView
                year={selectedMonthCell.year}
                month={(allMonths.indexOf(selectedMonthCell.month) + 1).toString()}
                collectorMissingDocuments={collectorMissingDocuments!}
                onClose={() => setOpenMonthView(false)}
              />
            </Box>
          </Dialog>
        </div>
      )}
      {isBillModalOpen && (
        <AddBillModal
          isOpen={isBillModalOpen}
          collectorId={selectedCollectorId}
          onClose={closeBillModal}
          onSaveButtonClick={handleUpdateMissingDocuments}
        />
      )}
    </Box>
  );
};
